import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  FlyoutDataType,
  FlyoutOpeningData,
  InterestProfileStatistics,
  PersonalData,
  PersonDetails,
  SessionAction,
  SessionDetails,
} from '../flyout-models';
import { FlyoutService } from '../flyout.service';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../core/language.service';
import { AccountService } from '../../account/account.service';
import { InterestProfile } from '../../account-settings/models/interest-profiles.model';
import { GravatarService } from '../../core/gravatar.service';
import { WidgetService } from 'app/shared/services/widget.service';
import TimeUtils from 'app/shared/utils/time-utils';

@Component({
  selector: 'app-flyout-headline',
  templateUrl: './flyout-headline.component.html',
  styleUrls: ['./flyout-headline.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutHeadlineComponent implements OnInit, OnDestroy {
  public icon: string;
  public title: string;
  public subTitle: string;
  public countryCode: string;
  public commitment: number;
  public firstVisit: string;
  public firstVisitTimestampNumber: number;
  public latestVisit: string;
  public secondsOnSite: number;
  public isSession = false;
  public interestProfileStatistics: Array<InterestProfileStatistics>;
  public img: string;
  public tags: string[] = [];
  private tagsSubscription: Subscription;
  public FlyoutDataType = FlyoutDataType;

  private _openingData: FlyoutOpeningData;
  private personDetails: PersonDetails;
  private sessionDetails: SessionDetails;
  private personSubscription: Subscription;
  private sessionSubscription: Subscription;
  private subscriptionArray: Subscription[] = [];
  @Input() public loadingData = false;
  @Input() public isMobile = false;

  @Input() public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this._openingData = openingData;
    this.updateRenderingProperties();
  }

  public get flyoutOpeningData(): FlyoutOpeningData {
    return this._openingData;
  }

  constructor(
    private readonly flyoutService: FlyoutService,
    private readonly accountService: AccountService,
    private readonly gravatarService: GravatarService,
    private readonly languageService: LanguageService,
    private readonly widgetService: WidgetService,
  ) {}

  ngOnInit() {
    this.personSubscription = this.flyoutService.personDetailsBS.subscribe((personDetails: PersonDetails) => {
      this.personDetails = personDetails;
      this.updateRenderingProperties();
    });
    this.sessionSubscription = this.flyoutService.sessionDetailsBS.subscribe((sessionDetails: SessionDetails) => {
      this.sessionDetails = sessionDetails;
      this.updateRenderingProperties();
    });
    this.tagsSubscription = this.widgetService.getAllTags().subscribe((tags) => {
      this.tags = tags.data;
    });
  }

  private loadAndUpdateInterestProfiles(interestProfileStatistics: Array<InterestProfileStatistics>) {
    if (!interestProfileStatistics || interestProfileStatistics.length === 0) {
      this.interestProfileStatistics = undefined;
      return;
    }
    this.accountService.getInterestProfiles().subscribe(
      (profiles: Array<InterestProfile>) => {
        if (profiles) {
          let sumOfExisting = 0;
          interestProfileStatistics.forEach((interestProfileStats: InterestProfileStatistics) => {
            const profileIndex = profiles.findIndex((p) => p.id === interestProfileStats.id);
            if (profileIndex !== -1) {
              interestProfileStats.name = profiles[profileIndex].name;
            } else {
              interestProfileStats.name = interestProfileStats.id;
            }
            sumOfExisting += interestProfileStats.value;
          });
          if (sumOfExisting > 0 && sumOfExisting < 100) {
            interestProfileStatistics.push({
              name: this.languageService.translateInstantly('generic.other'),
              value: 100 - sumOfExisting,
            });
          }
        }
        this.interestProfileStatistics = interestProfileStatistics;
      },
      (error) => {
        console.error('Error while loading interest profiles: ' + error);
      },
    );
  }

  public clickOnTitle() {
    if (
      (!this.flyoutOpeningData.identifier && !this.flyoutOpeningData.organization) ||
      this.flyoutOpeningData.type === FlyoutDataType.Contact
    ) {
      return;
    }
    if (this.flyoutOpeningData.identifier) {
      this.openContactFlyout(this.flyoutOpeningData.identifier);
    }
  }

  public closeFlyout() {
    this.flyoutService.close();
  }

  public clickOnSubtitle() {
    if (!this.flyoutOpeningData.identifier || this.flyoutOpeningData.type === FlyoutDataType.Contact) {
      return;
    }
    if (this.flyoutOpeningData.identifier) {
      this.openContactFlyout(this.flyoutOpeningData.identifier);
    }
  }

  public openContactFlyout(identifier: string) {
    this.flyoutService.open({
      type: FlyoutDataType.Contact,
      organization: this.flyoutOpeningData.organization,
      identifier: identifier,
      siteId: this.flyoutOpeningData.siteId,
      gravatarHash: this.flyoutOpeningData.gravatarHash,
      tags: this.tags,
      widgets: this.flyoutService.getWidgetsList(),
    } as FlyoutOpeningData);
  }

  private resetAllRenderableValues() {
    this.icon = undefined;
    this.img = undefined;
    this.title = undefined;
    this.subTitle = undefined;
    this.countryCode = undefined;
    this.commitment = undefined;
    this.firstVisit = undefined;
    this.latestVisit = undefined;
    this.isSession = undefined;
    this.secondsOnSite = undefined;
    this.firstVisitTimestampNumber = undefined;
  }

  private updateRenderingProperties() {
    this.resetAllRenderableValues();
    let countryCode, commitment, firstVisit, latestVisit, img, secondsOnSite;
    // Set initial data from flyout opening data
    if (this.flyoutOpeningData) {
      countryCode = this.flyoutOpeningData.countryCode;
      commitment = this.flyoutOpeningData.commitment;
      this.icon =
        this.flyoutOpeningData.type === FlyoutDataType.Session
          ? 'offline_bolt'
          : this.flyoutOpeningData.type === FlyoutDataType.Contact
            ? 'account_circle'
            : 'business';

      if (this.flyoutOpeningData.type === FlyoutDataType.Session && this.sessionDetails) {
        this.isSession = this.flyoutOpeningData.type === FlyoutDataType.Session;
        secondsOnSite = this.sessionDetails.timeOnSite;
        if (this.flyoutOpeningData.gravatarHash) {
          img = this.gravatarService.getCustomerProfilesUrl(this.flyoutOpeningData.gravatarHash, 'session');
        }
        this.setTitleAndSubtitleFromPersonalData(
          this.flyoutOpeningData.identifier,
          this.sessionDetails.personalData,
          this.languageService.translateInstantly('flyout.session') + ': #' + this.flyoutOpeningData.sessionId,
        );
        firstVisit = TimeUtils.getDateIsoFormat(this.sessionDetails.time);
        this.firstVisitTimestampNumber = this.getTimestampAsNumber(firstVisit);
        latestVisit = TimeUtils.getDateIsoFormat(this.parseLatestVisitFromSession());
        this.loadAndUpdateInterestProfiles(this.sessionDetails.interestProfiles);
      } else if (this.flyoutOpeningData.type === FlyoutDataType.Contact && this.personDetails) {
        this.isSession = false;
        if (this.flyoutOpeningData.gravatarHash) {
          img = this.gravatarService.getCustomerProfilesUrl(this.flyoutOpeningData.gravatarHash, 'person');
        }
        firstVisit = this.personDetails.firstSeen;
        latestVisit = this.personDetails.lastSeen;
        this.loadAndUpdateInterestProfiles(this.personDetails.interestProfiles);
        this.setTitleAndSubtitleFromPersonalData(
          this.flyoutOpeningData.identifier,
          this.personDetails.personalData,
          undefined,
        );
      }

      // Set commitment if it was not set from visit
      if (this.personDetails && !commitment && this.personDetails.commitment) {
        commitment = this.personDetails.commitment;
      }
    }
    this.img = img;
    this.countryCode = countryCode;
    this.commitment = commitment;
    this.firstVisit = firstVisit;
    this.latestVisit = latestVisit;
    this.secondsOnSite = secondsOnSite;
  }

  private setTitleAndSubtitleFromPersonalData(identifier: string, personalData: PersonalData, defaultTitle: string) {
    if (!personalData) {
      this.title = identifier ?? defaultTitle;
      this.subTitle = undefined;
      return;
    }
    const mainTitle = identifier ?? personalData.email;
    const name = this.getName(personalData);
    if (this.isSession && mainTitle) {
      this.subTitle = mainTitle;
    } else if (mainTitle && name) {
      this.title = name;
      this.subTitle = mainTitle;
    } else if (mainTitle) {
      this.title = mainTitle;
      this.subTitle = undefined;
    } else if (personalData.organization) {
      this.title = personalData.organization;
      this.subTitle = undefined;
    }
  }

  private getName(personalData: PersonalData): string {
    const firstName = personalData.firstName;
    const lastName = personalData.lastName;
    if (firstName || lastName) {
      if (firstName) {
        return firstName + (lastName ? ` ${lastName}` : '');
      } else {
        return lastName;
      }
    } else {
      return personalData.name;
    }
  }

  private parseLatestVisitFromSession(): string {
    if (!this.sessionDetails || !this.sessionDetails.actionsList) {
      return undefined;
    }
    return this.sessionDetails.actionsList.reduce(function (prev: SessionAction, curr: SessionAction) {
      return TimeUtils.isAfterDate(prev.time, curr.time) ? prev : curr;
    }).time;
  }

  ngOnDestroy(): void {
    this.subscriptionArray.push(this.personSubscription, this.tagsSubscription, this.sessionSubscription);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }

  private getTimestampAsNumber(date: string) {
    if (!date) {
      return undefined;
    }
    const utcDateConverted = new Date(date);
    const localDate = new Date(0); // The 0 there is the key, which sets the date to the epoch
    localDate.setUTCMilliseconds(utcDateConverted.getTime());
    return localDate.getTime();
  }
}
